import React, { useEffect, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, X, ArrowRight } from 'lucide-react';
interface ChecklistItem {
  id: string;
  title: string;
  helper: string;
  link: string;
}
const CHECKLIST_ITEMS: ChecklistItem[] = [{
  id: 'ask-ai',
  title: 'Ask your first question to LawVo AI',
  helper: 'Get instant legal answers',
  link: '#'
}, {
  id: 'upload-doc',
  title: 'Upload your first document',
  helper: 'Securely store documents',
  link: '/documents/upload'
}, {
  id: 'create-doc',
  title: 'Create your first legal document',
  helper: 'Use our templates',
  link: '/documents/create'
}, {
  id: 'review-doc',
  title: 'Request a document review',
  helper: 'Get expert feedback',
  link: '/documents/review'
}, {
  id: 'book-call',
  title: 'Book a call with a lawyer',
  helper: '24/7 availability',
  link: '/legal-help'
}, {
  id: 'complete-profile',
  title: 'Complete your profile',
  helper: 'Add name and photo',
  link: '/profile'
}];
export function OnboardingChecklist() {
  const [isClosed, setIsClosed] = useState(false);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedItems, setCompletedItems] = useState<string[]>([]);
  const [mounted, setMounted] = useState(false);
  // Load state from localStorage on mount
  useEffect(() => {
    const savedClosed = localStorage.getItem('lawvo_onboarding_closed');
    const savedTaskIndex = localStorage.getItem('lawvo_onboarding_current_task');
    const savedCompleted = localStorage.getItem('lawvo_onboarding_completed');
    if (savedClosed === 'true') setIsClosed(true);
    if (savedTaskIndex) {
      const index = parseInt(savedTaskIndex, 10);
      if (!isNaN(index) && index >= 0 && index < CHECKLIST_ITEMS.length) {
        setCurrentTaskIndex(index);
      }
    }
    if (savedCompleted) {
      try {
        setCompletedItems(JSON.parse(savedCompleted));
      } catch (e) {
        console.error('Failed to parse completed items', e);
      }
    }
    setMounted(true);
  }, []);
  // Persist state changes
  const handleClose = () => {
    setIsClosed(true);
    localStorage.setItem('lawvo_onboarding_closed', 'true');
  };
  const handlePrevTask = () => {
    const newIndex = Math.max(0, currentTaskIndex - 1);
    setCurrentTaskIndex(newIndex);
    localStorage.setItem('lawvo_onboarding_current_task', String(newIndex));
  };
  const handleNextTask = () => {
    const newIndex = Math.min(CHECKLIST_ITEMS.length - 1, currentTaskIndex + 1);
    setCurrentTaskIndex(newIndex);
    localStorage.setItem('lawvo_onboarding_current_task', String(newIndex));
  };
  const toggleItem = (id: string) => {
    const newCompleted = completedItems.includes(id) ? completedItems.filter(item => item !== id) : [...completedItems, id];
    setCompletedItems(newCompleted);
    localStorage.setItem('lawvo_onboarding_completed', JSON.stringify(newCompleted));
  };
  if (!mounted || isClosed) return null;
  const progress = completedItems.length;
  const total = CHECKLIST_ITEMS.length;
  const percent = progress / total * 100;
  const isAllComplete = progress === total;
  const currentTask = CHECKLIST_ITEMS[currentTaskIndex];
  const isCurrentTaskCompleted = completedItems.includes(currentTask.id);
  return <div className="border border-borderColor rounded-lg bg-white shadow-sm overflow-hidden w-full sm:w-auto" data-id="element-328">
      {/* Single compact line with all elements */}
      {isAllComplete ? <div className="flex items-center justify-between px-3 py-2" data-id="element-329">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0" data-id="element-330">
            <span className="text-xs font-medium text-secondary hidden sm:inline" data-id="element-331">
              Getting started
            </span>
            <a href="#" className="group flex items-center gap-1.5 text-xs font-medium text-violetBlue hover:text-[#6844E8] transition-colors truncate" data-id="element-332">
              <span className="truncate" data-id="element-333">
                You're all set. Start with LawVo AI
              </span>
              <ArrowRight className="w-3 h-3 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" data-id="element-334" />
            </a>
          </div>
          <button onClick={handleClose} className="p-1 text-disabled hover:text-secondary hover:bg-aliceBlue rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-violetBlue/20 flex-shrink-0" aria-label="Close getting started guide" data-id="element-335">
            <X className="w-3.5 h-3.5" data-id="element-336" />
          </button>
        </div> : <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2" data-id="element-337">
          {/* Label - Hidden on mobile */}
          <span className="text-xs font-medium text-secondary whitespace-nowrap hidden sm:inline" data-id="element-338">
            Getting started
          </span>

          {/* Progress bar - Smaller on mobile */}
          <div className="flex items-center gap-1.5 sm:gap-2 w-[70px] sm:w-[100px]" data-id="element-339">
            <span className="text-[10px] text-secondary font-medium whitespace-nowrap" data-id="element-340">
              {progress}/{total}
            </span>
            <div className="h-1 flex-1 bg-aliceBlue rounded-full overflow-hidden" data-id="element-341">
              <div className="h-full bg-violetBlue rounded-full transition-all duration-500 ease-out" style={{
            width: `${percent}%`
          }} data-id="element-342" />
            </div>
          </div>

          {/* Divider - Hidden on mobile */}
          <div className="h-3 w-px bg-borderColor hidden sm:block" data-id="element-343" />

          {/* Previous button */}
          <button onClick={handlePrevTask} disabled={currentTaskIndex === 0} className="p-1 text-disabled hover:text-secondary hover:bg-aliceBlue rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-disabled focus:outline-none focus:ring-2 focus:ring-violetBlue/20 flex-shrink-0" aria-label="Previous task" data-id="element-344">
            <ChevronLeft className="w-3.5 h-3.5" data-id="element-345" />
          </button>

          {/* Task number indicator */}
          <span className="text-[10px] text-secondary font-medium whitespace-nowrap" data-id="element-346">
            {currentTaskIndex + 1}/{total}
          </span>

          {/* Current task */}
          <div className="flex items-center gap-1.5 sm:gap-2 cursor-pointer group w-[180px] sm:w-[240px]" onClick={() => toggleItem(currentTask.id)} data-id="element-347">
            <div className="flex-shrink-0" data-id="element-348">
              <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all duration-200 ${isCurrentTaskCompleted ? 'bg-violetBlue border-violetBlue' : 'bg-white border-disabled group-hover:border-violetBlue'}`} data-id="element-349">
                {isCurrentTaskCompleted && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-350" />}
              </div>
            </div>

            <div className="flex-1 min-w-0" data-id="element-351">
              <div className={`text-xs font-medium transition-colors truncate ${isCurrentTaskCompleted ? 'text-secondary line-through decoration-disabled' : 'text-secondary group-hover:text-primary'}`} data-id="element-352">
                {currentTask.title}
              </div>
            </div>
          </div>

          {/* Next button */}
          <button onClick={handleNextTask} disabled={currentTaskIndex === CHECKLIST_ITEMS.length - 1} className="p-1 text-disabled hover:text-secondary hover:bg-aliceBlue rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-disabled focus:outline-none focus:ring-2 focus:ring-violetBlue/20 flex-shrink-0" aria-label="Next task" data-id="element-353">
            <ChevronRight className="w-3.5 h-3.5" data-id="element-354" />
          </button>

          {/* Close button */}
          <button onClick={handleClose} className="p-1 text-disabled hover:text-secondary hover:bg-aliceBlue rounded transition-colors focus:outline-none focus:ring-2 focus:ring-violetBlue/20 ml-0.5 flex-shrink-0" aria-label="Close getting started guide" data-id="element-355">
            <X className="w-3.5 h-3.5" data-id="element-356" />
          </button>
        </div>}
    </div>;
}